<template>
  <div>
    <v-data-table
      class="main-table"
      :headers="headers"
      :items="items"
      hide-default-footer
      disable-sort
      :options="tableOptions"
    >
      <template v-slot:item="{ item, index }">
        <tr :key="`users-table-${index}`">
          <td>{{ item.firstName }}</td>
          <td>{{ item.lastName }}</td>
          <td>{{ item.username }}</td>
          <td>{{ item.email }}</td>
          <td class="text-right">
            <dropdown
              :options="['Edit', 'Delete']"
              @edit="
                logEvent('Clicked button to edit user');
                setUserForEdit(item);
              "
              @delete="
                logEvent('Clicked button to delete user');
                checkBeforeDelete(item);
              "
            />
          </td>
        </tr>
      </template>
    </v-data-table>

    <div class="text-right mt-4">
      <yellow-button
        @click="
          logEvent('Clicked button to add user');
          isUpdate = false;
          selectedUser = {};
        "
        >Add User</yellow-button
      >
    </div>

    <v-dialog v-model="deleteDialogOpen" width="672">
      <v-card class="pa-4">
        <v-card-title class="justify-center pt-0">
          <v-icon size="48" color="#FE285B">mdi-alert-rhombus</v-icon>
          <v-btn icon absolute top right @click="closeDeleteDialog">
            <v-icon>mdi-window-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <h1 class="dialog-main-message">
            Remove
            {{
              selectedUserForDelete == null
                ? ""
                : selectedUserForDelete.firstName +
                  " " +
                  selectedUserForDelete.lastName
            }}?
          </h1>
          Removing this user permanently removes them from all access to
          VendorProof Engage resources. Are you sure you want to do this?
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDeleteDialog">
            Cancel
          </v-btn>

          <v-btn color="primary" @click="actuallyRemoveUser">
            Remove User
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog :value="selectedUser" persistent max-width="666">
      <foldable-card
        non-foldable
        hide-border
        @closing="selectedUser = null"
        :title="isUpdate ? 'Edit User Information' : 'Add New User'"
      >
        <v-form
          @submit.prevent="
            saveUser(selectedUser);
            selectedUser = null;
          "
          v-model="formValid"
          v-if="selectedUser"
        >
          <v-row no-gutters class="mb-2">
            <v-col cols="4 pr-3">
              <v-text-field
                filled
                dense
                v-model="selectedUser.firstName"
                label="First Name*"
                :rules="[rules.required('First Name')]"
              ></v-text-field>
            </v-col>
            <v-col cols="4 pl-3">
              <v-text-field
                filled
                dense
                v-model="selectedUser.lastName"
                label="Last Name*"
                :rules="[rules.required('Last Name')]"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row no-gutters class="mb-2" v-if="!isUpdate">
            <v-col cols="8">
              <v-text-field
                filled
                dense
                v-model="selectedUser.username"
                label="Login Name*"
                :rules="[rules.required('Login Name')]"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row no-gutters class="mb-2">
            <v-col cols="8">
              <v-text-field
                filled
                dense
                v-model="selectedUser.email"
                label="Email*"
                :rules="[rules.required('Email'), rules.email]"
              ></v-text-field>
            </v-col>
          </v-row>

          <template
            v-if="
              !isUpdate ||
                (currentUser && currentUser.username === selectedUser.username)
            "
          >
            <v-row no-gutters class="mb-2">
              <v-col cols="8">
                <v-text-field
                  filled
                  type="password"
                  ref="password1"
                  dense
                  v-model="selectedUser.password"
                  :label="isUpdate ? 'Password' : 'Password*'"
                  :rules="[
                    passwordMatchRule,
                    isUpdate || rules.required('Password')
                  ]"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row no-gutters class="mb-2">
              <v-col cols="8">
                <v-text-field
                  filled
                  type="password"
                  ref="password2"
                  dense
                  v-model="selectedUser.passwordConfirm"
                  :label="isUpdate ? 'Confirm Password' : 'Confirm Password*'"
                  :rules="[
                    passwordMatchRule,
                    isUpdate || rules.required('Confirm Password')
                  ]"
                ></v-text-field>
              </v-col>
            </v-row>
          </template>

          <v-card-actions class="pa-0">
            <v-spacer></v-spacer>
            <v-btn
              text
              class="text-decoration-underline mr-2"
              @click="selectedUser = null"
              >Cancel</v-btn
            >
            <v-btn type="submit" color="primary" :disabled="!formValid"
              >Save User</v-btn
            >
          </v-card-actions>
        </v-form>
      </foldable-card>
    </v-dialog>
  </div>
</template>
<script>
import { get, put, post, _delete } from "@/services/request";
import { usersAPI, userByIdAPI } from "@/services/api";
import FoldableCard from "@/components/FoldableCard";
import * as rules from "@/services/rules";
import cloneDeep from "lodash/cloneDeep";
import hasNotifications from "@/mixins/has-notifications";
import hasCurrentUser from "@/mixins/has-current-user";
import YellowButton from "@/components/providertrust/buttons/SmallYellowButton";
import Dropdown from "@/components/Dropdown";

const tableOptions = {
  itemsPerPage: -1
};

const headers = [
  { text: "First Name", value: "firstName" },
  { text: "Last Name", value: "lastName" },
  { text: "User Name", value: "username" },
  { text: "Email", value: "email" },
  { text: "", value: "", align: "end" }
];
export default {
  name: "Users",
  mixins: [hasNotifications, hasCurrentUser],
  data: () => ({
    tableOptions,
    items: [],
    headers,
    selectedUserForDelete: null,
    deleteDialogOpen: false,
    selectedUser: null,
    isUpdate: null,
    formValid: false,
    rules
  }),
  created() {
    this.loadUsers();
  },
  methods: {
    showItemDropdown(item, val) {
      // hide all dropdowns first if we are showing a particular dropdown
      if (val) this.items.forEach(item => this.$set(item, "_show", false));
      this.$set(item, "_show", val);
    },
    setUserForEdit(user) {
      // this is to prevent intemediate changes in the from gets rendered in the table before saving it
      const userClone = cloneDeep(user);
      this.isUpdate = true;
      this.selectedUser = userClone;
    },

    passwordMatchRule() {
      const password1 = this.$refs.password1;
      const password2 = this.$refs.password2;

      const val1 = this.selectedUser.password || "";
      const val2 = this.selectedUser.passwordConfirm || "";

      const errorMsg = "Passwords don't match";

      if (val1 !== val2) return errorMsg;

      if (password1 && password1.errorBucket.includes(errorMsg)) {
        // rerun validation to update validation status
        setTimeout(() => password1.validate(), 0);
      }
      if (password2 && password2.errorBucket.includes(errorMsg)) {
        // rerun validation to update alidation status
        setTimeout(() => password2.validate(), 0);
      }
      return true;
    },

    loadUsers() {
      get(usersAPI).then(response => {
        if (response && response.data) {
          this.items = response.data;
        }
      });
    },
    saveUser({ id, firstName, lastName, password, email, username }) {
      let promise;
      const payload = {
        firstName,
        lastName,
        password,
        email
      };
      if (this.isUpdate) {
        promise = put(userByIdAPI.getUrl(id), payload).then(
          () => this.showMessage("User is updated successfully."),
          error => {
            if (error.response) {
              this.showMessage({
                mode: "error",
                text: `Update user failed. ${error.response.data}`
              });
            }
          }
        );
      } else {
        promise = post(usersAPI, {
          username,
          ...payload
        }).then(
          () => this.showMessage("User is added successfully."),
          error => {
            if (error.response) {
              this.showMessage({
                mode: "error",
                text: `Add user failed. ${error.response.data}`
              });
            }
          }
        );
      }

      promise.finally(() => {
        this.loadUsers();
      });
    },
    checkBeforeDelete(item) {
      if (this.items.length === 1) {
        this.showMessage({
          mode: "error",
          text: `There is only one user in your account. You cannot delete this user unless another user is added.`
        });
      } else {
        this.openDeleteDialog(item);
      }
    },
    actuallyRemoveUser() {
      if (this.selectedUserForDelete == null) return;

      let id = this.selectedUserForDelete.id;
      var name =
        this.selectedUserForDelete.firstName +
        " " +
        this.selectedUserForDelete.lastName;

      _delete(userByIdAPI.getUrl(id))
        .then(() => {
          this.closeDeleteDialog();
          this.loadUsers();
          this.showMessage(`User ${name} removed`);
        })
        .catch(() => {
          this.closeDeleteDialog();
          this.showMessage({
            text: "User could not be removed",
            mode: "error"
          });
        });
    },
    closeDeleteDialog() {
      this.selectedUserForDelete = null;
      this.deleteDialogOpen = false;
    },
    openDeleteDialog(item) {
      this.selectedUserForDelete = item;
      this.deleteDialogOpen = true;
    }
  },
  components: {
    FoldableCard,
    YellowButton,
    Dropdown
  }
};
</script>

<style lang="sass" scoped>
.main-table ::v-deep
  .v-data-table__wrapper
    overflow: visible
.dialog-main-message
  margin-top: 3px
  margin-bottom: 26px
  color: $darker-grey
  font-size: 20px
  font-weight: bold
  line-height: 25px
</style>
